import { useRef, useState } from "react";
import { uploadMedia, UploadCancelled, type MediaKind } from "@/services/mediaUpload";

type Props = {
  kind: MediaKind;
  accept?: string;
  label?: string;
  onUploaded: (r: { url: string; durationSeconds: number | null; fileName: string }) => void;
};

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 / 1024))} MB`;
}

export default function VideoUploader({ kind, accept = "video/mp4,video/webm,video/quicktime", label = "Subir video desde tu computador", onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [size, setSize] = useState(0);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (file: File) => {
    setError(null);
    if (!/^video\/|^image\//.test(file.type)) return setError("Elige un archivo de video (.mp4, .webm o .mov).");
    setFileName(file.name);
    setSize(file.size);
    setPct(0);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await uploadMedia(file, kind, setPct, ctrl.signal);
      onUploaded({ url: r.url, durationSeconds: r.durationSeconds, fileName: file.name });
      setFileName(null);
    } catch (err) {
      if (!(err instanceof UploadCancelled)) setError(err instanceof Error ? err.message : "No se pudo subir el archivo.");
      setFileName(null);
    } finally {
      setBusy(false);
      abortRef.current = null;
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void start(f); }} />
      {busy ? (
        <div className="rounded-xl border border-primary-300 bg-primary-500/5 p-3">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="font-semibold text-foreground-800 truncate">{fileName} · {formatSize(size)}</span>
            <button type="button" onClick={() => abortRef.current?.abort()} className="text-accent-700 font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
          </div>
          <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
            <div className="h-full bg-primary-500 transition-all" style={{ width: `${pct}%` }}></div>
          </div>
          <div className="mt-1 text-[11px] text-foreground-500">Subiendo… {pct}% — no cierres esta pestaña hasta que termine.</div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-primary-400 bg-primary-500/5 px-4 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-500/10 cursor-pointer">
          <i className="ri-upload-cloud-2-line text-lg"></i> {label}
        </button>
      )}
      {error ? <p className="mt-1.5 text-xs text-accent-700">{error}</p> : null}
    </div>
  );
}
